import { observer } from 'mobx-react';
import { Link, useLocation } from 'react-router-dom';
import moment from 'moment';
import cn from 'classnames';
import { Box } from 'uiKit/atoms/Box/Box';
import { Card } from 'uiKit/atoms/Card/Card';
import { CardContent } from 'uiKit/atoms/CardContent/CardContent';
import { CardMedia } from 'uiKit/atoms/CardMedia/CardMedia';
import { Typography } from 'uiKit/atoms/Typography/Typography';
import { Col } from 'uiKit/atoms/Col/Col';
import { Row } from 'uiKit/atoms/Row/Row';
import { Divider } from 'uiKit/atoms/Divider/Divider';
import { LOT_STATUS } from '../../../../constants';
import mockPhoto from 'assets/img/camera_alt.svg';
import { ReactComponent as HeartOutlinedSvg } from 'assets/img/heart-outlined.svg';
import { ReactComponent as HeartFilledSvg } from 'assets/img/heart-filled.svg';
import { lotTypesEnum } from 'constants/enums';
import { useStores } from 'stores';
import styles from './LotCard.module.scss';
import { getLotLocationTitle, formatConvertedPrice } from 'helpers/utils';
import { Tooltip } from 'uiKit/atoms/Tooltip/Tooltip';

const LotCard = observer(({ lot }) => {
  const {
    lotStore: { increaseFavotiteLotTotalOn, addToFavorite, removeFromFavorite },
    userStore,
  } = useStores();
  const location = useLocation();

  const isFavoritePage = location.pathname === '/favorites';

  const handleAddToFavorites = lotId => {
    increaseFavotiteLotTotalOn(1);
    addToFavorite(lotId);
  };

  const handleDeleteFromFavorites = lotId => {
    increaseFavotiteLotTotalOn(-1);
    removeFromFavorite(lotId, isFavoritePage);
  };

  const renderBadges = () => {
    const { lotType, lotStatus, isHighlighted } = lot;
    const badgeColor = lotType === 'Offer' ? styles.bns3_lot_card_badge_offer : styles.bns3_lot_card_badge_lookingfor;
    const inactiveBadgeColor = styles.bns3_lot_card_badge_inactive;
    const inactiveLot = lotStatus !== LOT_STATUS.PUBLISHED;
    const isDraft = lotStatus === LOT_STATUS.DRAFT;

    return (
      <div className={cn(styles.bns3_lot_card_badges_container)}>
        {isHighlighted && (
          <div className={cn(styles.bns3_lot_card_badge, styles.bns3_lot_card_badge_highlithed)}>
            <span>NEW ARRIVAL!</span>
          </div>
        )}
        <div className={cn({ [styles.bns3_inactive_lot]: inactiveLot })}>
          <span className={cn(styles.bns3_lot_card_badge, badgeColor)}>{lotTypesEnum[lotType]}</span>
          {inactiveLot && (
            <span className={cn(styles.bns3_lot_card_badge, inactiveBadgeColor)}>{isDraft ? 'Draft' : 'Deactivated'}</span>
          )}
        </div>
      </div>
    );
  };

  const renderFavorite = () => {
    if (userStore.userProfile.id === lot.userId) return null;

    return (
      <div className={cn(styles.bns3_lot_card_favourite_container)}>
        {lot.isFavorited ? (
          <button
            type="button"
            onClick={e => {
              e.preventDefault();
              handleDeleteFromFavorites(lot.id);
            }}
            className={cn(styles.bns3_lot_like_icon, styles.bns3_lot_is_liked)}
          >
            <HeartFilledSvg />
          </button>
        ) : (
          <button
            type="button"
            onClick={e => {
              e.preventDefault();
              handleAddToFavorites(lot.id);
            }}
            className={cn(styles.bns3_lot_like_icon, styles.bns3_lot_is_not_liked)}
          >
            <HeartOutlinedSvg />
          </button>
        )}
      </div>
    )
  };

  return (
    <Col className={cn(styles.bns3_lot_card_container, { [styles.bns3_lot_card_container_highlithed]: lot.isHighlighted })}>
      <Link
        to={{
          pathname: `/lot/${lot.id}`,
          state: { from: { location } },
        }}
        className={styles.bns3_lot_card_link}
      >
        {renderBadges()}
        {renderFavorite()}

        <Card variant='outlined' className={styles.bns3_lot_card}>
          <Box variant='outlined'>
            <CardMedia
            component="img"
            image={lot.image ? lot.image.fileName : mockPhoto}
            alt="example"
            className={cn(styles.bns3_lot_card_image, {
              [styles.bns3_inactive_lot_deactivated]: lot.lotStatus === LOT_STATUS.DEACTIVATED,
              [styles.bns3_inactive_lot]: lot.lotStatus === LOT_STATUS.DRAFT,
            })}
          />
          </Box>
          

          <CardContent className={styles.bns3_lot_card_descr}>
            <Typography className={styles.bns3_lot_card_product_type}>
              <span className={ styles.bns3_lot_card_product_type}>{lot.productType.toUpperCase()}</span>
            </Typography>

            <Tooltip title={lot.title} placement="bottom">
                <Typography className={styles.bns3_lot_card_title} variant="h7" component="span">
                  {lot.title}
                </Typography>
            </Tooltip>
           {lot.fixedPrice ? (
              <div
                className={cn(styles.bns3_lot_card_price, styles.bns3_lot_card_price_fixed, {
                  [styles.bns3_inactive_lot]: lot.lotStatus !== LOT_STATUS.PUBLISHED,
                })}
              >
                {`${formatConvertedPrice(lot.fixedPrice)} ${lot.currency}`}
              </div>
            ) : (
              <div
                className={cn(styles.bns3_lot_card_price, styles.bns3_lot_card_price_free, {
                  [styles.bns3_inactive_lot]: lot.lotStatus !== LOT_STATUS.PUBLISHED,
                })}
              >
                For Free
              </div>
            )}

            <div className={styles.bns3_lot_card_info_wrapper}>
              <Divider className={styles.bns3_lot_card_divider} />
              <Row justifyContent="space-between">
                <Col className={styles.bns3_lot_card_info}>{getLotLocationTitle(lot)}</Col>
                <Col className={styles.bns3_lot_card_info}>{moment(lot.added).format('ll')}</Col>
              </Row>
            </div>
          </CardContent>
         
        </Card>
      </Link>
    </Col>
  );
});

export default LotCard;